import styled from "styled-components";
import React, { useState, useEffect, useRef } from "react";

const PDFBtn = ({
  width = "auto",
  height = "auto",
  handler,
  isNewsBookMarked,
  widthOfView,
  newsData,
  isAuthed,
  isDisabled,
  showText = true,
}) => {
  // Track last click time
  const lastClickTime = useRef(0);
  const ACTIVE_DURATION = 2000; // 2 seconds in ms
  
  // States for UI
  const [currentState, setCurrentState] = useState('default');
  const [isHovering, setIsHovering] = useState(false);
  const isMobile = /Mobi/.test(window.navigator.userAgent);
  
  // Timer for active state
  const activeTimerRef = useRef(null);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (activeTimerRef.current) {
        clearTimeout(activeTimerRef.current);
      }
    };
  }, []);
  
  // Set initial state based on props
  useEffect(() => {
    if (isNewsBookMarked) {
      setCurrentState('active');
    } else {
      setCurrentState('default');
    }
  }, [isNewsBookMarked]);

  // Calculate current visual state
  const getVisualState = () => {
    // If disabled, always show disabled
    if (!isAuthed || isDisabled) {
      return 'disabled';
    }
    
    // If bookmarked, always show active
    if (isNewsBookMarked) {
      return 'active';
    }
    
    // If recently clicked, show active
    const now = Date.now();
    if (now - lastClickTime.current < ACTIVE_DURATION) {
      return 'active';
    }
    
    // Otherwise, respect hover state
    if (isHovering && !isMobile) {
      return 'hover';
    }
    
    // Default state
    return 'default';
  };

  // Handle click
  const handleClick = () => {
    if (!handler || isDisabled || !isAuthed) return;
    
    // Update last click time
    lastClickTime.current = Date.now();
    
    // Force active state
    setCurrentState('active');
    
    // Clear any existing timer
    if (activeTimerRef.current) {
      clearTimeout(activeTimerRef.current);
    }
    
    // Set timer to revert to default after duration
    activeTimerRef.current = setTimeout(() => {
      // Only revert if not bookmarked and still in active state
      if (!isNewsBookMarked && currentState === 'active') {
        setCurrentState(isHovering ? 'hover' : 'default');
      }
    }, ACTIVE_DURATION);
    
    // Call the handler
    handler();
  };

  // Mouse events
  const handleMouseEnter = () => {
    setIsHovering(true);
    
    // Only update state if not in active state and not recently clicked
    const now = Date.now();
    if (currentState !== 'active' && now - lastClickTime.current >= ACTIVE_DURATION) {
      setCurrentState('hover');
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    
    // Only update state if not in active state and not recently clicked
    const now = Date.now();
    if (currentState !== 'active' && now - lastClickTime.current >= ACTIVE_DURATION) {
      setCurrentState('default');
    }
  };

  // Render loading spinner
  const renderLoadingSpinner = () => {
    return (
      <LoadingSpinner>
        <div className="spinner"></div>
      </LoadingSpinner>
    );
  };

  // PDF Icon SVG component
  const PDFIcon = ({ fill = "#637282", size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path 
        d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" 
        stroke={fill} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
      />
      <path 
        d="M14 2V8H20" 
        stroke={fill} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
      />
      <path 
        d="M16 13H8V15H16V13Z" 
        fill={fill}
      />
      <path 
        d="M16 17H8V19H16V17Z" 
        fill={fill}
      />
    </svg>
  );

  // Render button content based on visual state
  const renderButtonContent = () => {
    const visualState = getVisualState();
    
    let iconFill, textColor, buttonStyle;
    
    switch (visualState) {
      case 'disabled':
        iconFill = "#C2D0D7";
        textColor = "#C2D0D7";
        buttonStyle = {
          backgroundColor: "#F5F7F8",
          border: "1px solid #E8EBEB",
          opacity: 0.6
        };
        break;
        
      case 'hover':
        iconFill = "#689AB9";
        textColor = "#689AB9";
        buttonStyle = {
          // backgroundColor: "#F8FBFC",
          border: "1px solid #689AB9",
          transform: "translateY(-1px)",
          // boxShadow: "0 4px 12px rgba(104, 154, 185, 0.15)"
        };
        break;
        
      case 'active':
        iconFill = "#00C3FF";
        textColor = "#00C3FF";
        buttonStyle = {
          backgroundColor: "#F0FCFF",
          border: "1px solid #00C3FF",
          // boxShadow: "0 2px 8px rgba(0, 195, 255, 0.2)"
        };
        break;
        
      case 'default':
      default:
        iconFill = "#637282";
        textColor = "#637282";
        buttonStyle = {
          backgroundColor: "#FFFFFF",
          border: "1px solid #E1E8ED"
        };
        break;
    }

    return (
      <ButtonContent style={buttonStyle} iconOnly={!showText}>
        <IconWrapper>
          <PDFIcon fill={iconFill} size={20} />
        </IconWrapper>
        {showText && (
          <ButtonText style={{ color: textColor }}>
            Open PDF
          </ButtonText>
        )}
        {visualState === 'active' && isNewsBookMarked && renderLoadingSpinner()}
      </ButtonContent>
    );
  };

  return (
    <PDFBtnWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      id="pdf-exporter"
      style={{ cursor: "pointer" }}
      className={isMobile ? 'mobile' : ''}
      data-state={currentState}
    >
      {renderButtonContent()}
    </PDFBtnWrapper>
  );
};

export default PDFBtn;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 16px;
  transition: all 0.2s ease-in-out;
  position: relative;
  min-width: ${props => props.iconOnly ? '44px' : '120px'};
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  
  ${props => props.iconOnly && `
    padding: 10px;
    min-width: 44px;
    width: 44px;
    // height: 44px;
  `}
  
  &:hover {
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease-in-out;
  white-space: nowrap;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  
  .spinner {
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 195, 255, 0.3);
    border-radius: 50%;
    border-top-color: #00C3FF;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PDFBtnWrapper = styled.div`
  background: transparent;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &.mobile {
    ${ButtonContent} {
      min-width: 100px;
      padding: 6px 12px;
      
      ${ButtonText} {
        font-size: 13px;
      }
    }
  }
`;

export const EditButton = styled.img`
  cursor: pointer;
  background-color: transparent;
  color: transparent;
  width: 150px;
  height: 30px;
`;
